import type { IRegisterMapProps } from "@/sync/syncTypes"
import { clearRosterStore, removeRosterUser, replaceRosterUsers, upsertRosterUser } from "@/stores/roster.store"
import { inflateMapItem } from "@/sync/helpers"
import consola from "consola"

function getRosterKeyFromRaw(rawData: unknown, fallbackData: unknown) {
  const maybeKey = (rawData as any)?.item?.key ?? (rawData as any)?.key
  if (maybeKey != null) return `${maybeKey}`

  const maybeID = (fallbackData as any)?.id
  if (maybeID != null) return `${maybeID}`

  return null
}

async function bootstrapRosterMap(mapName: string) {
  const syncMap = window.ringioFrontV3?.twilio?.sync?.initialization?.maps?.roster as any
  if (!syncMap) {
    return
  }

  try {
    const users: Record<string, unknown> = {}

    let page = await syncMap.getItems({ pageSize: 100 })
    while (page) {
      const items = Array.isArray(page.items) ? page.items : []
      for (const item of items) {
        const key = item?.key != null ? `${item.key}` : null
        if (!key) continue

        const data = item?.data?.deflated ? inflateMapItem(item.data) : item?.data
        users[key] = data ?? {}
      }

      if (!page.hasNextPage) break
      page = await page.nextPage()
    }

    replaceRosterUsers(users)
  } catch (error) {
    consola.error(`Failed to bootstrap roster map ${mapName}`, error)
  }
}

export const RosterMapHandler = (name: string): IRegisterMapProps => ({
  name,
  onFailure: (error: unknown) => {
    consola.error(`Map ${name} failed`, error)
  },
  onRemoved: () => {
    consola.debug(`Map removed: ${name}`)
    clearRosterStore()
  },
  onItemAdded: (data: unknown, rawData: unknown) => {
    const key = getRosterKeyFromRaw(rawData, data)
    if (!key) {
      consola.warn(`Ignoring roster add without key for map ${name}`, data)
      return
    }

    upsertRosterUser(key, data)
  },
  onItemUpdated: (data: unknown, rawData: unknown) => {
    const key = getRosterKeyFromRaw(rawData, data)
    if (!key) {
      consola.warn(`Ignoring roster update without key for map ${name}`, data)
      return
    }

    upsertRosterUser(key, data)
  },
  onItemRemoved: (data: unknown, rawData: unknown) => {
    const key = getRosterKeyFromRaw(rawData, data)
    if (!key) {
      consola.warn(`Ignoring roster removal without key for map ${name}`, data)
      return
    }

    removeRosterUser(key)
  },
  onConnected: () => {
    void bootstrapRosterMap(name)
  },
})
